import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./styles/FileUploadHubspot.css";
import api, { API_ENDPOINTS, PARAMS } from '../api/axiosConfig';
import useTicketId from '../hooks/useTicketId';
import CryptoJS from 'crypto-js';

// Encryption key for ticket ID
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'lutdocs-demo-key';

function decryptTicketId(token) {
  try {
    const bytes = CryptoJS.AES.decrypt(decodeURIComponent(token), ENCRYPTION_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

export default function FileUploadHubspot() {
  // Always call hooks unconditionally
  const location = useLocation();
  const ticketIdFromHook = useTicketId();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  // Use state for ticketId
  const [ticketId, setTicketId] = useState(token ? null : ticketIdFromHook);

  const expiresAt = Number(searchParams.get('expiresAt'));
  const [expired, setExpired] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [valid, setValid] = useState(null); // null: loading, false: invalid, true: valid
  const [comments, setComments] = useState("");
  const [userName, setUserName] = useState("");
  const fileInputRef = useRef();
  const navigate = useNavigate();

  const MAX_FILE_SIZE_MB = 100;

  // Validate one-time token on mount (and set ticketId before checking validity)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
    if (token) {
      setValid(null);
      api.get(API_ENDPOINTS.TOKEN_VALIDATE, { params: { token } })
        .then(res => {
          // Expect TokenValidationResponse: { ticketId, status }
          if (res.data && res.data.status === 'VALID') {
            setValid(true);
            if (res.data.ticketId) {
              setTicketId(res.data.ticketId);
            }
          } else {
            setValid(false);
            setStatus({ type: 'error', text: 'Invalid or expired link.' });
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 410) {
            setStatus({ type: 'error', text: 'This link has already been used.' });
          } else {
            setStatus({ type: 'error', text: 'Invalid or expired link.' });
          }
          setValid(false);
        });
    }
  }, [location.search]);

  // Check ticket validity only if ticketId is present and not from token flow
  useEffect(() => {
    if (expiresAt && Date.now() > expiresAt) {
      setExpired(true);
      return;
    }
    // Only check validity if there is no token in the URL (i.e., direct ticketId flow)
    if (!token && ticketId) {
      async function checkValidity() {
        setValid(null);
        try {
          console.log("Checking ticket validity for:", ticketId);
          const res = await api.get(API_ENDPOINTS.TICKET_VALIDITY, {
            params: { [PARAMS.TICKET_ID]: ticketId },
          });
          const data = res.data;
          console.log("Ticket validity API response:", data);
          setValid(data === true || data.valid === true);
        } catch (err) {
          console.error("Ticket validity check failed:", err);
          setValid(false);
        }
      }
      checkValidity();
    }
  }, [ticketId, expiresAt, token]);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    const tooLarge = selected.find(file => file.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (tooLarge) {
      setStatus({ type: 'error', text: `File "${tooLarge.name}" exceeds 100MB limit. Please select a smaller file.` });
      setFiles([]);
      // Optionally, show a browser alert as well:
      alert(`File "${tooLarge.name}" exceeds 100MB limit. Please select a smaller file.`);
      return;
    }
    if (selected.length > 0) {
      setFiles(selected);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    const tooLarge = dropped.find(file => file.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (tooLarge) {
      setStatus({ type: 'error', text: `File "${tooLarge.name}" exceeds 100MB limit. Please select a smaller file.` });
      setFiles([]);
      alert(`File "${tooLarge.name}" exceeds 100MB limit. Please select a smaller file.`);
      return;
    }
    if (dropped.length > 0) {
      setFiles(dropped);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setStatus(null);
    if (!userName.trim()) {
      setStatus({ type: 'error', text: 'Please enter your name before submitting.' });
      setUploading(false);
      return;
    }
    console.log("Uploading files:", files, "for ticket:", ticketId);
    try {
      let res, data;
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      const endpoint =
        files.length > 1
          ? API_ENDPOINTS.UPLOAD_MULTIPLE
          : API_ENDPOINTS.UPLOAD;
      if (files.length === 1) {
        formData.delete("files");
        formData.append("file", files[0]);
      }
      // formData.append(PARAMS.TICKET_ID, ticketId);
      // Add comment to formData if present (API expects 'comment', not 'comments')
      let commentToSend = comments && comments.trim().length > 0
        ? `${comments.trim()} \n Added by - ${userName.trim()}`
        : ` Added by - ${userName.trim()}`;
      formData.append("comment", commentToSend);
      console.log("Comment to send:", commentToSend);
      console.log("FormData entries:", Array.from(formData.entries()));
      res = await api.post(endpoint + `?${PARAMS.TICKET_ID}=${encodeURIComponent(ticketId)}`, formData);
      data = res.data;
      console.log("Upload API response:", data);
      if (files.length > 1) {
        // BulkDocumentResponse: { message, successfulUploads, errors }
        if (!res.status === 200 || !data.successfulUploads) {
          const errorMsg = data && data.message ? data.message : "Bulk upload failed";
          setStatus({ type: "error", text: errorMsg });
          throw new Error(errorMsg);
        }
        // If all files failed (no successfulUploads), redirect to failed page
        if (!data.successfulUploads || data.successfulUploads.length === 0) {
          navigate(`/failed?ticketId=${ticketId}`);
          return;
        }
        // Only pick name and id for each successful upload
        const minimalUploads = data.successfulUploads.map(({ id, name }) => ({ id, name }));
        const bulkResponse = encodeURIComponent(JSON.stringify({
          message: data.message,
          successfulUploads: minimalUploads,
          errors: data.errors || []
        }));
        navigate(`/thankyou?ticketId=${ticketId}&bulkResponse=${bulkResponse}`);
      } else {
        // DocumentResponse: { message, result }
        if (!res.status === 200 || !data.result) {
          const errorMsg = data && data.message ? data.message : "Upload failed";
          setStatus({ type: "error", text: errorMsg });
          throw new Error(errorMsg);
        }
        // Success: redirect
        navigate(`/thankyou?ticketId=${ticketId}`);
      }
    } catch (err) {
      let errorText = err?.message || "Upload failed. Please try again.";
      // If error response from API, show its message
      if (err.response && err.response.data && err.response.data.message) {
        errorText = err.response.data.message;
      }
      setStatus({
        type: "error",
        text: errorText,
      });
    } finally {
      setUploading(false);
    }
  };

  if (expired) {
    return (
      <div className="container">
        <div className="card">
          <div className="header">
            <h1>Link Expired</h1>
          </div>
          <div className="content">
            <p>This secure upload link has expired. Please request a new link from the portal.</p>
          </div>
        </div>
      </div>
    );
  }

  if (valid === null) {
    return (
      <div className="container">
        <div className="card">
          <div className="header">
            <h1>Checking ticket validity...</h1>
          </div>
        </div>
      </div>
    );
  }
  if (valid === false) {
    return (
      <div className="container">
        <div className="card">
          <div className="header">
            <h1>Invalid or expired ticket</h1>
          </div>
          <div className="content">
            <p>Your ticket is not valid for document upload.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="header">
          <h1>Upload Supporting Documents</h1>
          <div className="ticket-id-display">Ticket ID: #{ticketId}</div>
        </div>

        <div className="content">
          <div className="upload-header">
            <h3>Evidence Required</h3>
            <p>Upload your supporting documents</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="input-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="userName" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                Your Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                id="userName"
                name="userName"
                type="text"
                value={userName}
                onChange={e => setUserName(e.target.value)}
                required
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                disabled={uploading}
                placeholder="Enter your name"
              />
            </div>

            <div
              className="file-upload"
              onClick={() => fileInputRef.current.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <div className="file-icon">📁</div>
              <p>Drag & drop files here or click to browse</p>

              {files.length > 0 && (
                <ul id="file-name">
                  {files.map((file, idx) => (
                    <li key={idx}>
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </li>
                  ))}
                </ul>
              )}

              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileSelect}
                required
              />
            </div>

            {/* Comments Section - to be integrated with API later */}
            <div className="comments-section" style={{ marginTop: '2rem' }}>
              <label htmlFor="comments" style={{ fontWeight: 'bold', display: 'block', marginBottom: '0.5rem' }}>
                Comments (optional)
              </label>
              <textarea
                id="comments"
                name="comments"
                rows={4}
                placeholder="Add any comments or notes for the reviewer..."
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical' }}
                disabled={uploading}
                value={comments}
                onChange={e => setComments(e.target.value)}
              />
            </div>

            <button
              type="submit"
              className="submit-btn"
              aria-label="Submit documents"
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  Uploading <span className="loading" role="status" aria-live="polite"></span>
                </>
              ) : (
                "Submit Documents"
              )}
            </button>
          </form>

          {status && (
            <div className={`status-message ${status.type}`}>
              {status.text}
            </div>
          )}
        </div>
      </div>

      <footer>
        <p>HubSpot Dispute-Document Upload Portal &copy; 2025</p>
      </footer>
    </div>
  );
}
